import type { VersionString, ViewID } from "@bestowinc/tinkertoy-core";

import type { LoadedView, ViewLoader } from "./types";

// ============================================================================
// Deduped loads (one promise per viewId + version)
// ============================================================================

function makeKey(viewId: ViewID, version: VersionString): string {
  return `${viewId}:${version}`;
}

const loadPromises = new Map<string, Promise<LoadedView>>();
const loaded = new Map<string, LoadedView>();

/**
 * Same cached promise for every subscriber of a `(viewId, version)`.
 *
 * Mirrors `getModuleLoadPromise` — `TinkertoyShell` subscribes from an effect
 * (no Suspense) and a remount for the same view collapses onto the in-flight
 * import rather than re-fetching.
 */
export function getViewLoadPromise(
  loader: ViewLoader,
  viewId: ViewID,
  version: VersionString,
): Promise<LoadedView> {
  const key = makeKey(viewId, version);
  let p = loadPromises.get(key);
  if (!p) {
    p = loader.loadView(viewId, version);
    const pending = p;
    // Evict failed loads so a transient error (CDN blip, network) doesn't
    // permanently brick the view for the rest of the session. Identity-guard
    // the delete so a late-firing rejection can't clobber a fresh promise
    // inserted under the same key after a cache reset.
    void pending.then(
      result => {
        if (loadPromises.get(key) === pending) {
          loaded.set(key, result);
        }
      },
      () => {
        if (loadPromises.get(key) === pending) {
          loadPromises.delete(key);
        }
      },
    );
    loadPromises.set(key, p);
  }
  return p;
}

/**
 * The already-loaded view for `viewId@version`, if its load has settled. The
 * shell loader awaits the view bundle before returning, so the shell can read
 * the definition synchronously on the commit that renders it.
 */
export function peekLoadedView(
  viewId: ViewID,
  version: VersionString,
): LoadedView | undefined {
  return loaded.get(makeKey(viewId, version));
}

/**
 * Clears the view load dedupe cache.
 *
 * @remarks Not part of the supported public API for apps — tests (and rare dev
 * tooling) import this to reset module-scope state between cases.
 *
 * @internal
 */
export function resetViewReadResourceCache(): void {
  loadPromises.clear();
  loaded.clear();
}
